import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { ApiErrorResponse } from '@app/shared/models/api-error-response';
import { ApiResponse } from '@app/shared/models/api-response';
import { Constants } from '@app/shared/models/constants';
import { Language } from '@app/shared/models/language';
import { AuthService } from '@app/shared/services/auth.service';
import { StorageService } from '@app/shared/services/storage.service';
import { TranslateService } from '@app/shared/translate/translate.service';
import { CurrentUserInfoSharingService } from '@app/users/services/current-user-info-sharing.service';
import { UserProfileApiService } from '@app/users/services/user-profile-api.service';
import { lastValueFrom } from 'rxjs';

@Component({
	selector: 'load-user-language',
	template: '',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoadUserLanguageComponent implements OnInit {
	constructor (
		private readonly translateService: TranslateService,
		private readonly authService: AuthService,
		private readonly storageService: StorageService,
		private readonly userProfileApiService: UserProfileApiService,
		private readonly currentUserInfoSharingService: CurrentUserInfoSharingService
	) { }

	ngOnInit () {
		this.authService
			.onAuthenticationSucceed
			.subscribe(() => this.loadLanguage());
	}

	private async loadLanguage (): Promise<void> {


		const userProfileCultureName = this.authService.getUserProfile().language as string;
		const langFromLogin = this.storageService.getItem(Constants.LoginLanguageStorageKey, 'session');

		// Let's deal with any language selected from login screen.
		if (await this.handleLanguageFromLogin(langFromLogin, userProfileCultureName)) {
			return;
		}

		// ...otherwise, let's deal with the user's language.
		this.handleUserLanguage(userProfileCultureName);
	}

	private async handleLanguageFromLogin (langFromLogin: string | null, userProfileCultureName: string): Promise<boolean> {
		if (!langFromLogin) {
			return false;
		}

		let selectedLanguage: Language;
		try {
			selectedLanguage = JSON.parse(langFromLogin) as Language;
		} catch (error) {
			console.error(`Error parsing language from login:`, error);
			return false;
		}

		if (selectedLanguage.cultureName === userProfileCultureName) {
			return true;
		}

		try {
			await this.updateUserLanguage(selectedLanguage.cultureName);
		} catch (error) {
			console.error(`Error updating user language:`, error);
			return false;
		}
		this.currentUserInfoSharingService.updateUserInfo({ language: selectedLanguage });
		this.storageService.removeItem(Constants.LoginLanguageStorageKey, 'session');
		return true;
	}

	private async updateUserLanguage (cultureName: string): Promise<ApiResponse<void> | ApiErrorResponse> {
		const { id } = this.authService.getUserProfile();
		return lastValueFrom(this.userProfileApiService.updateUserLanguage({ userId: id, cultureName }));
	}

	private handleUserLanguage (userProfileCultureName: string): void {

		const appLanguageCultureName = this.translateService.getAppLanguage().cultureName;

		if (!userProfileCultureName || appLanguageCultureName === userProfileCultureName) {
			return;
		}

		const userLanguage = this.translateService.getLanguageByCultureName(userProfileCultureName);

		if (userLanguage) {
			this.translateService.switchToLanguage(userLanguage);
		} else {
			console.warn(`Language ${ userProfileCultureName } not found in tenant languages.`);
		}
	}
}